import React, { useCallback, useEffect, useState } from 'react';

import {
  ProgressoSubtarefas,
  SubtarefaDoVeiculo,
  marcarSubtarefa,
  obterProgressoDoVeiculo,
} from '../../../api/subtarefasApi';
import { notify } from '../../../shared/feedback/notify';
// at-90: a conversa sobre uma parte do serviço nasce na linha dela.
import ComentariosDoVeiculo from '../../../components/comentarios/ComentariosDoVeiculo';
// ⚠️ Import EXPLÍCITO: a classe `comentarios-subtarefa-indicador` do botão abaixo vive nesse
// arquivo. Contar com o CSS que o componente importado carrega é acoplamento invisível — é o
// que originou o BUG-V74 na at-67.
import '../../../components/comentarios/ComentariosDoVeiculo.css';
import './SubtarefasDoVeiculo.css';
import '../../../shared/ui/modal-close-button.css'; // X padrão do sistema

interface SubtarefasDoVeiculoProps {
  carId: string;
  /** Bloqueia a marcação durante janela de pausa automática / fora do expediente (at-48b). */
  bloqueado?: boolean;
  motivoBloqueio?: string | null;
  /** Avisa a tela quando o progresso muda, para ela recontar pendências ao finalizar. */
  onProgressoAtualizado?: (progresso: ProgressoSubtarefas) => void;
}

/**
 * Sub-tarefas da esteira ATUAL do veículo, marcáveis pelo colaborador (at-89, ADR 0041).
 *
 * 🔴 Cada marcação exige a senha do próprio colaborador (decisão do Victor, 02/09/2026): o
 * registro afirma que uma pessoa específica fez aquele serviço, e é o que a Recepção repassa ao
 * associado. O modal descreve o que vai acontecer antes de confirmar (requisito transversal do
 * ADR 0021).
 *
 * ⚠️ Marcar NÃO conclui o serviço da esteira, e deixar sub-tarefa sem marcar não impede finalizar
 * — ela fica pendente e visível.
 */
const SubtarefasDoVeiculo: React.FC<SubtarefasDoVeiculoProps> = ({
  carId,
  bloqueado,
  motivoBloqueio,
  onProgressoAtualizado,
}) => {
  const [progresso, setProgresso] = useState<ProgressoSubtarefas | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [falhou, setFalhou] = useState(false);
  const [alvo, setAlvo] = useState<{ sub: SubtarefaDoVeiculo; concluir: boolean } | null>(null);
  // at-90: qual linha tem a conversa aberta. Uma por vez — a lista fica no card do veículo.
  const [comentandoId, setComentandoId] = useState<string | null>(null);
  const [senha, setSenha] = useState('');
  const [salvando, setSalvando] = useState(false);

  const carregar = useCallback(async () => {
    if (!carId) return;
    setCarregando(true);
    try {
      const dados = await obterProgressoDoVeiculo(carId);
      setProgresso(dados);
      setFalhou(false);
      onProgressoAtualizado?.(dados);
    } catch (error) {
      setProgresso(null);
      setFalhou(true);
      notify.errorFrom(
        error,
        'Não foi possível carregar as sub-tarefas deste veículo. Recarregue a página; se persistir, acione a TI.',
      );
    } finally {
      setCarregando(false);
    }
  }, [carId, onProgressoAtualizado]);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  const esteiraAtual = progresso?.esteiras.find((e) => e.atual) ?? null;

  const fecharModal = () => {
    setAlvo(null);
    setSenha('');
  };

  const confirmar = async () => {
    if (!alvo || !senha) return;

    setSalvando(true);
    try {
      const atualizado = await marcarSubtarefa(carId, alvo.sub.definicaoId, alvo.concluir, senha);
      setProgresso(atualizado);
      onProgressoAtualizado?.(atualizado);
      notify.success(
        alvo.concluir
          ? `"${alvo.sub.nome}" registrada como concluída.`
          : `"${alvo.sub.nome}" voltou para pendente.`,
      );
      fecharModal();
    } catch (error) {
      // O modal continua aberto para corrigir a senha. Nenhum toast de sucesso sai daqui —
      // marcar e falhar não pode parecer que deu certo (at-60).
      notify.errorFrom(error, 'Não foi possível registrar a sub-tarefa.');
    } finally {
      setSalvando(false);
    }
  };

  if (carregando) {
    return <div className="subtarefas-veiculo-carregando">Carregando sub-tarefas...</div>;
  }

  if (falhou) {
    return (
      <div className="subtarefas-veiculo">
        <p className="subtarefas-veiculo-erro">
          Não foi possível carregar as sub-tarefas deste veículo. O que você já marcou continua
          registrado — recarregue a página para ver a lista.
        </p>
      </div>
    );
  }

  // Esteira atual sem sub-tarefas configuradas: nada a mostrar.
  if (!esteiraAtual || esteiraAtual.total === 0) return null;

  return (
    <div className="subtarefas-veiculo">
      <div className="subtarefas-veiculo-cabecalho">
        <span className="subtarefas-veiculo-titulo">O que já foi feito?</span>
        <span className="subtarefas-veiculo-contador">
          {esteiraAtual.totalConcluidas} de {esteiraAtual.total}
        </span>
      </div>

      {bloqueado && motivoBloqueio && (
        <p className="subtarefas-veiculo-bloqueio">{motivoBloqueio}</p>
      )}

      <ul className="subtarefas-veiculo-lista">
        {esteiraAtual.subtarefas.map((sub) => (
          <li key={sub.definicaoId} className="subtarefas-veiculo-item">
            <label className="subtarefas-veiculo-label" title={sub.nome ?? undefined}>
              <input
                type="checkbox"
                checked={sub.concluida}
                disabled={bloqueado || salvando}
                onChange={() => setAlvo({ sub, concluir: !sub.concluida })}
              />
              <span className={`subtarefas-veiculo-nome ${sub.concluida ? 'concluida' : ''}`}>
                {sub.nome}
              </span>
            </label>

            {/*
              at-90: a conversa sobre ESTA parte do serviço, aberta pela própria linha (decisão do
              Victor: comentário de sub-tarefa nasce onde a sub-tarefa aparece).

              O componente decide sozinho se mostra o campo de escrita — pelas permissões
              efetivas da matriz, que vêm na resposta dele. Aqui não se replica regra de papel.
            */}
            {sub.atividadeSubtarefaId && (
              <button
                type="button"
                className="comentarios-subtarefa-indicador"
                aria-expanded={comentandoId === sub.atividadeSubtarefaId}
                onClick={() =>
                  setComentandoId((atual) =>
                    atual === sub.atividadeSubtarefaId ? null : sub.atividadeSubtarefaId,
                  )
                }
              >
                {comentandoId === sub.atividadeSubtarefaId ? 'Fechar comentários' : 'Comentários'}
              </button>
            )}

            {comentandoId === sub.atividadeSubtarefaId && (
              <ComentariosDoVeiculo
                carId={carId}
                apenasSubtarefaId={sub.atividadeSubtarefaId}
              />
            )}
          </li>
        ))}
      </ul>

      {alvo && (
        <div className="controle-producao-modal-overlay" onClick={fecharModal}>
          <div className="controle-producao-modal" onClick={(e) => e.stopPropagation()}>
            <div className="controle-producao-modal-header">
              <h3>{alvo.concluir ? 'Confirmar sub-tarefa concluída' : 'Reabrir sub-tarefa'}</h3>
              <button className="close-button" onClick={fecharModal} type="button">
                &times;
              </button>
            </div>

            <div className="controle-producao-modal-body">
              <p className="servico-desfecho-aviso" style={{ margin: '0 0 16px' }}>
                {alvo.concluir ? (
                  <>
                    Você está registrando que <strong>{alvo.sub.nome}</strong> foi concluída, em{' '}
                    <strong>{esteiraAtual.esteiraNome}</strong>. O registro fica com o seu nome e a
                    data, e passa a aparecer para a Recepção, para o Chefe de Oficina e para o setor
                    de Eventos. Isto <strong>não</strong> encerra o serviço da esteira — você segue
                    trabalhando normalmente.
                  </>
                ) : (
                  <>
                    <strong>{alvo.sub.nome}</strong> volta a constar como pendente, e o registro de
                    conclusão anterior é desfeito.
                  </>
                )}
              </p>

              <div className="controle-producao-filter-field">
                <label>
                  Sua senha <span style={{ color: '#ef4444' }}>*</span>
                </label>
                <input
                  type="password"
                  className="controle-producao-modal-input"
                  autoComplete="current-password"
                  placeholder="Digite sua senha de login"
                  value={senha}
                  autoFocus
                  onChange={(e) => setSenha(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && senha) void confirmar();
                  }}
                />
              </div>
            </div>

            <div className="controle-producao-filters-modal-actions">
              <button className="controle-producao-btn-clear" onClick={fecharModal} type="button">
                Cancelar
              </button>
              <button
                className="controle-producao-btn-apply"
                onClick={() => void confirmar()}
                disabled={!senha || salvando}
                type="button"
              >
                {salvando ? 'Registrando...' : 'Confirmar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SubtarefasDoVeiculo;
